import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet';

import { InputWithConfirm, RadioOptionsWithConfirm, IQuestion } from 'shared-components';

import { ApplicationState } from '../../../../../store';
import * as StudentActions from '../../../../../store/student/actions';
import * as VPFActions from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import {
  createNavigateBackwardAction,
  createNavigateForwardAction,
  createNavigateToAction
} from '../../../../../middleware/actions';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../../store/ui/actions';
import { titleAppend } from '../../../../../utilities/head';

const VisaPrepFormAddHistoryPage1 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => {
    dispatch(StudentActions.saveVisaPrepForm(Pages.PAGE6));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(VPFActions.createUpdateVPIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  // state props from store
  const studentVisaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);
  const {
    hasCriminalCharges,
    hasTerrorismInvolvement,
    hasTerrorismConnection,
    hasTerrorismJustified,
    hasWarCrimes,
    hasBadActivities,
    language
  } = studentVisaPrepForm;

  // constants
  const currentStep = 1;
  const steps = 1;
  const sectionTitle = 'Declarations';

  // component state
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasCriminalCharges,
      hasTerrorismInvolvement,
      hasTerrorismConnection,
      hasTerrorismJustified,
      hasWarCrimes,
      hasBadActivities,
      language
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    complete,
    hasCriminalCharges,
    hasTerrorismInvolvement,
    hasTerrorismConnection,
    hasTerrorismJustified,
    hasWarCrimes,
    hasBadActivities,
    language
  ]);

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onPrevious={onPrevious}
      onNext={onNext}
      onSkip={onSkip}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <RadioOptionsWithConfirm
        key={VQ.HAS_CRIMINAL_CHARGES}
        id={VQ.HAS_CRIMINAL_CHARGES}
        label={
          'Have you ever been charged in any country with a criminal offence for which you have not yet been tried in the court (including traffic offences)?'
        }
        onClick={(question: IQuestion) => onChange(VQ.HAS_CRIMINAL_CHARGES, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasCriminalCharges === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasCriminalCharges === false
          }
        ]}
      />

      <RadioOptionsWithConfirm
        key={VQ.HAS_TERRORISM_INVOLVEMENT}
        id={VQ.HAS_TERRORISM_INVOLVEMENT}
        label={'Have you ever been involved in, supported or encouraged terrorist activities in any country?'}
        onClick={(question: IQuestion) => onChange(VQ.HAS_TERRORISM_INVOLVEMENT, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasTerrorismInvolvement === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasTerrorismInvolvement === false
          }
        ]}
      />

      <RadioOptionsWithConfirm
        key={VQ.HAS_TERRORISM_CONNECTION}
        id={VQ.HAS_TERRORISM_CONNECTION}
        label={
          'Have you ever been a member of, or given support to, an organisation that has been connected with terrorism?'
        }
        onClick={(question: IQuestion) => onChange(VQ.HAS_TERRORISM_CONNECTION, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasTerrorismConnection === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasTerrorismConnection === false
          }
        ]}
      />

      <RadioOptionsWithConfirm
        key={VQ.HAS_TERRORISM_JUSTIFIED}
        id={VQ.HAS_TERRORISM_JUSTIFIED}
        label={
          'Have you ever, by any means or medium, expressed views that justify or glorify terrorist violence, or that may encourage others to commit acts of terrorism or other serious criminal acts?'
        }
        onClick={(question: IQuestion) => onChange(VQ.HAS_TERRORISM_JUSTIFIED, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasTerrorismJustified === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasTerrorismJustified === false
          }
        ]}
      />

      <RadioOptionsWithConfirm
        key={VQ.HAS_WAR_CRIMES}
        id={VQ.HAS_WAR_CRIMES}
        label={
          'In times of either peace or war have you ever been involved in, or suspected of involvement in, war crimes, crimes against humanity or genocide?'
        }
        onClick={(question: IQuestion) => onChange(VQ.HAS_WAR_CRIMES, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasWarCrimes === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasWarCrimes === false
          }
        ]}
      />

      <RadioOptionsWithConfirm
        key={VQ.HAS_BAD_ACTIVITIES}
        id={VQ.HAS_BAD_ACTIVITIES}
        label={'Have you engaged in any other activities that might indicate that you may not be admitted to the UK?'}
        onClick={(question: IQuestion) => onChange(VQ.HAS_BAD_ACTIVITIES, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasBadActivities === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasBadActivities === false
          }
        ]}
      />

      <InputWithConfirm
        key={VQ.LANGUAGE}
        id={VQ.LANGUAGE}
        value={language}
        label={'If UKVI need to interview you, what language would you like to use in the interview?'}
        onChange={(evt: any) => {
          onChange(VQ.LANGUAGE, evt.currentTarget.value);
        }}
      />
    </QuestionPage>
  );
};
export default VisaPrepFormAddHistoryPage1;
